import React, { useEffect, useState } from "react";
import { Button, Form, Row, Col, Modal } from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import { withTranslation } from "react-i18next";
import * as Yup from "yup";
import { indianMobileSchema } from "../../helpers/mobileValidation";
import CommonMobileInput from "../../components/Common/CommonMobileInput";
import callApi from "../../helpers/CallApi";
import {
  createSubDistributor,
  getSubDistributor,
  updateSubDistributor,
} from "../../helpers/API";
import useApi from "../../Hooks/useApi";
import { toast } from "react-toastify";

const Add = ({ state, handleClose, refresh, t }) => {
  const { operation, id, show } = state;
  const [disabled, setDisabled] = useState(operation == "view");

  // Dynamic validation schema using translations
  const getValidationSchema = () => {
    return Yup.object().shape({
      name: Yup.string().required(
        t("subDistributor.add.validation.nameRequired")
      ),
      email: Yup.string()
        .email(t("subDistributor.add.validation.emailInvalid"))
        .required(t("subDistributor.add.validation.emailRequired")),
      mobile: indianMobileSchema(t, {
        requiredMessage: t("subDistributor.add.validation.mobileRequired"),
        invalidMessage: t("subDistributor.add.validation.mobileInvalid"),
      }),
    });
  };

  const initialValues = {
    name: "",
    email: "",
    mobile: "",
  };
  const [formData, setFormData] = useState(initialValues);

  useApi({
    callback: getSubDistributor,
    payload: id,
    dependencies: [id],
    shouldCallApi: Boolean(id),
    onSuccess: (res) => {
      const { name, email, mobile } = res;
      setFormData({
        name: name || "",
        email: email || "",
        mobile: String(mobile || ""),
      });
    },
  });

  useEffect(() => {
    setDisabled(operation === "view");
  }, [operation]);

  const handleSubmit = (values) => {
    const payload = {
      name: values.name,
      email: values.email,
      mobile: values.mobile,
    };

    callApi({
      callback: !id ? createSubDistributor : updateSubDistributor,
      payload: id ? [id, payload] : [payload],
      onSuccess: ({ message }) => {
        refresh((prev) => !prev);
        toast.success(
          message ||
            (!id
              ? t("subDistributor.add.messages.createSuccess")
              : t("subDistributor.add.messages.updateSuccess"))
        );
        handleClose();
      },
      onError: () => {
        toast.error(t("subDistributor.add.messages.error"));
      },
    });
  };

  const getHeading = () => {
    return t(`subDistributor.add.headings.${operation}`);
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>{getHeading()}</Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={formData}
        enableReinitialize
        validationSchema={getValidationSchema()}
        onSubmit={handleSubmit}
        validateOnBlur
        validateOnChange
      >
        {({ errors, touched }) => (
          <FormikForm>
            <Modal.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="required">
                      {t("subDistributor.add.labels.name")}
                    </Form.Label>
                    <Field
                      name="name"
                      disabled={disabled}
                      className={`form-control ${
                        touched.name && errors.name ? "is-invalid" : ""
                      }`}
                      placeholder={t(
                        "subDistributor.add.placeholders.enterName"
                      )}
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="invalid-feedback"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="required">
                      {t("subDistributor.add.labels.email")}
                    </Form.Label>
                    <Field
                      name="email"
                      type="email"
                      disabled={disabled}
                      className={`form-control ${
                        touched.email && errors.email ? "is-invalid" : ""
                      }`}
                      placeholder={t(
                        "subDistributor.add.placeholders.enterEmail"
                      )}
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="invalid-feedback"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-1">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="required">
                      {t("subDistributor.add.labels.mobileNumber")}
                    </Form.Label>
                    <CommonMobileInput
                      name="mobile"
                      disabled={disabled}
                      placeholder={t("subDistributor.add.placeholders.enterMobile")}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>
                {disabled
                  ? t("subDistributor.add.buttons.close")
                  : t("subDistributor.add.buttons.cancel")}
              </Button>
              {!disabled && (
                <Button variant="primary" type="submit">
                  {t("subDistributor.add.buttons.save")}
                </Button>
              )}
            </Modal.Footer>
          </FormikForm>
        )}
      </Formik>
    </Modal>
  );
};

export default withTranslation()(Add);
